import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { Box, Typography } from "@mui/material";
import type { CSSProperties, FC } from "react";
import { Fragment, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { NurseFullRole } from "../../../../utils/models";
import { SystemDropdown } from "../../../UIToolkit/Inputs/SystemDropDown";
import SystemDialog from "../../../UIToolkit/Popups/Dialog";
import { PORTAL_OVERLAY_ATTRIBUTE } from "../../../UIToolkit/Popups/portalOverlay";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { registerWalkthroughDismiss, unregisterWalkthroughDismiss } from "../../../UIToolkit/Walkthrough/walkthroughDismiss";
import { DepartmentFilterRow } from "./DepartmentFilterRow";
import { MessageBox } from "./MessageBox";
import { MessageFilter, type MessageFiltersType, type MessageWorkingFilterEnum } from "./MessageFilter";
import { useBroadcastMessageState } from "./useBroadcastMessageState";

const headingTypographyStyle: CSSProperties = {
	fontWeight: 600,
	fontSize: "26px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: "primary.main",
};

export type NewBroadcastMessageProps = {
	/** `didSend` is `true` only when a message was actually sent — a discard or a back-out passes nothing. */
	onClose: (didSend?: boolean) => void;
	nurseIdsToIgnore?: number[];
	initialRolesToSelect?: NurseFullRole[];
	initialFilters?: MessageFiltersType[];
	defaultWorkingFilter?: MessageWorkingFilterEnum;
	initialSmsMessage?: string;
	isMobile?: boolean;
	addAppLink?: boolean;
	initialSelectedNurseIds?: number[];
	lockRecipientSelection?: boolean;
	jobId?: string;
	allowFacilityScope?: boolean;
};

export const NewBroadcastMessage: FC<NewBroadcastMessageProps> = (props) => {
	const { t } = useTranslation();
	const {
		filters,
		nurseEmploymentDropdownItems,
		selectedRoles,
		handleRolesChange,
		selectedEmploymentTypes,
		handleEmploymentTypesChange,
		selectedNurseList,
		nursesOptions,
		isRosterLoading,
		isRosterError,
		onDropdownSelectionChange,
		messageValue,
		handleMessageChange,
		showBackWarning,
		toggleBackWarning,
		handleCancelOrEscape,
		isLoading,
		sendingDisabled,
		handleSendMessage,
		addFilterClicked,
		deleteFilterClicked,
		handleFilterChanged,
		availableRoles,
		isSuccess,
		availableJobs,
		selectedJobIds,
		handleJobsChange,
		showJobsFilter,
		jobFilterActive,
		facilityScope,
	} = useBroadcastMessageState(props);
	const { isFacilityAnnouncement, facilityName, departmentOptions } = facilityScope;
	const showRecipientFilters = !props.lockRecipientSelection;
	/** Anchors the Escape listeners below: they attach to the modal shell this composer is portaled into. */
	const composerRef = useRef<HTMLDivElement>(null);
	/** The one keypress both Escape listeners may see, so it is acted on once. */
	const handledEscape = useRef<KeyboardEvent | null>(null);

	// Tell a finishing Annie walkthrough how to put this composer away. Neither generic fallback can close
	// it: the backdrop is deliberately inert, Escape over a draft only raises "Discard message?" rather
	// than closing anything, and there is no CloseIcon here for the ✕ fallback to find. Left unregistered,
	// a tour ending inside the composer would simply strand it open.
	// `onClose()` with no argument is a DISCARD, never a send: only `handleSendMessage` passes `true`.
	const dismissForWalkthrough = useCallback(() => props.onClose(), [props.onClose]);
	useEffect(() => {
		registerWalkthroughDismiss(dismissForWalkthrough);
		return () => {
			unregisterWalkthroughDismiss(dismissForWalkthrough);
		};
	}, [dismissForWalkthrough]);

	// Escape mirrors Cancel: it closes an empty composer outright and raises "Discard message?" over a
	// typed draft. That takes TWO listeners, because the key arrives by two different routes:
	//
	//  - `document`, when focus is still OUTSIDE the composer. Nothing moves it in: MUI's FocusTrap gets
	//    a `ThemeProvider` as the Modal's child and so never reaches a DOM node to focus, leaving focus
	//    on whatever opened the composer. That Escape never enters the modal at all.
	//  - the modal shell, once you click into the message box. MUI's Modal then swallows Escape at its
	//    own root ("in case someone is listening for the escape key on the body" — `useModal`), so
	//    `document` never sees it. A native listener on the shell still does: MUI's is a React handler,
	//    dispatched later from the portal container above it.
	//
	// Nested floating surfaces keep their own Escape — a dropdown panel and the filter's time menu are
	// portaled out of this subtree and stop the key at <body>, below both listeners.
	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			// While the discard dialog is up, Escape belongs to it (MUI's Dialog dismisses it).
			if (event.key !== "Escape" || showBackWarning) return;
			if (handledEscape.current === event) return;
			handledEscape.current = event;
			handleCancelOrEscape();
		};
		const shell = composerRef.current?.closest<HTMLElement>(`[${PORTAL_OVERLAY_ATTRIBUTE}]`) ?? composerRef.current;
		shell?.addEventListener("keydown", handleEscape);
		document.addEventListener("keydown", handleEscape);
		return () => {
			shell?.removeEventListener("keydown", handleEscape);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [handleCancelOrEscape, showBackWarning]);

	return (
		<Box
			ref={composerRef}
			sx={{
				display: "flex",
				height: "100%",
				width: "100%",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					justifyContent: props.isMobile ? "start" : "space-between",
					gap: 3,
					padding: props.isMobile ? 2 : 5,
					bgcolor: "#fff",
					borderRadius: 2,
					height: props.isMobile ? "100%" : "fit-content",
					width: "100%",
				}}
			>
				<Typography style={headingTypographyStyle}>{t("broadcast.title")}</Typography>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						gap: 2,
						borderBottom: props.isMobile ? "1px solid #E3E4F0" : "none",
					}}
				>
					{showRecipientFilters && (
						<>
							{/* Annie walkthrough anchor. Deliberately this one-row Box and not the modal: a portal
							    component's attribute lands on the full-screen Modal root, which leaves the tooltip
							    nowhere to sit. It renders only when recipients are selectable — the punch-notify and
							    hiring call sites lock the selection, and the step correctly drops there. */}
							<Box
								data-annie-target="broadcast-recipients"
								sx={{
									display: "flex",
									gap: 2,
									width: "100%",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								{showJobsFilter && (
									<SystemDropdown
										items={availableJobs}
										selectedItems={selectedJobIds}
										multiple={true}
										onChangeMultiple={handleJobsChange}
										formControlSx={{ width: "95%" }}
										label={t("job.filter")}
										data-testid="broadcast-job-filter"
									/>
								)}
								<SystemDropdown
									items={availableRoles}
									selectedItems={selectedRoles}
									multiple={true}
									onChangeMultiple={handleRolesChange}
									formControlSx={{ width: "95%" }}
									label={t("role.filter")}
									data-testid="broadcast-role-filter"
								/>
								<SystemDropdown
									items={nurseEmploymentDropdownItems}
									selectedItems={selectedEmploymentTypes}
									multiple={true}
									onChangeMultiple={handleEmploymentTypesChange}
									formControlSx={{ width: "95%" }}
									label={t("broadcast.employment.type.filter")}
								/>
							</Box>
							{jobFilterActive && (
								<Typography variant="caption" sx={{ color: "text.secondary" }} data-testid="broadcast-job-filter-hint">
									{t("recipients.filtered.by.job")}
								</Typography>
							)}
						</>
					)}
					{departmentOptions && (
						<DepartmentFilterRow options={departmentOptions} pickedOptionIds={facilityScope.pickedOptionIds} onChange={facilityScope.onDepartmentsPicked} />
					)}
					{facilityScope.departmentsUnavailable && (
						<Typography variant="caption" sx={{ color: "text.secondary" }} data-testid="broadcast-departments-unavailable">
							{t("broadcast.departments.unavailable")}
						</Typography>
					)}
					{filters.length > 0 && !isFacilityAnnouncement && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
								height: "fit-content",
								flexWrap: "wrap",
							}}
						>
							{filters.map((filterAndId, index) => (
								<Fragment key={filterAndId.id}>
									{index > 0 && (
										<Typography
											sx={{
												fontSize: "14px",
												lineHeight: "100%",
												fontWeight: 400,
											}}
										>
											{t("broadcast.filter.and")}
										</Typography>
									)}
									<MessageFilter
										filterUuid={filterAndId.id}
										onDelete={deleteFilterClicked}
										onChange={handleFilterChanged}
										{...filterAndId.filterData}
										isMobile={props.isMobile}
									/>
								</Fragment>
							))}
						</Box>
					)}
					{showRecipientFilters && !isFacilityAnnouncement && (
						<SystemButton buttonVariant={"borderless"} leadingIcon={<AddOutlinedIcon />} onClick={addFilterClicked} sx={{ fontWeight: 600 }}>
							{t("broadcast.add.filter")}
						</SystemButton>
					)}
				</Box>
				{!props.isMobile ? (
					<Box
						style={{
							border: "1px solid #E3E4F0",
						}}
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							gap: 1,
							padding: 2,
							borderRadius: 1,
							width: "100%",
							color: "primary.dark",
						}}
					>
						<MessageBox
							messageValue={messageValue}
							handleMessageChange={handleMessageChange}
							selectedNurseList={selectedNurseList}
							nursesOptions={nursesOptions}
							onDropdownSelectionChange={onDropdownSelectionChange}
							lockRecipientSelection={props.lockRecipientSelection}
							isRosterLoading={isRosterLoading}
							isRosterError={isRosterError}
							announcement={
								isFacilityAnnouncement && facilityName !== undefined
									? { recipientCount: facilityScope.recipientCount, countFailed: facilityScope.recipientCountFailed, facilityName }
									: undefined
							}
						/>
					</Box>
				) : (
					<MessageBox
						messageValue={messageValue}
						handleMessageChange={handleMessageChange}
						selectedNurseList={selectedNurseList}
						nursesOptions={nursesOptions}
						onDropdownSelectionChange={onDropdownSelectionChange}
						isMobile={props.isMobile}
						lockRecipientSelection={props.lockRecipientSelection}
						isRosterLoading={isRosterLoading}
						isRosterError={isRosterError}
						announcement={
							isFacilityAnnouncement && facilityName !== undefined
								? { recipientCount: facilityScope.recipientCount, countFailed: facilityScope.recipientCountFailed, facilityName }
								: undefined
						}
					/>
				)}

				<Box
					sx={{
						display: "flex",
						gap: 2,
						width: "100%",
						justifyContent: "flex-end",
					}}
				>
					<SystemButton buttonVariant={"secondary"} onClick={handleCancelOrEscape}>
						{t("cancel")}
					</SystemButton>
					{/* Annie walkthrough anchor. It is `disabled` until a message is typed AND a recipient is
					    selected, so a tour reaching it finds it disabled — the step's copy describes it rather than
					    asking for a click, and the tour never presses it. */}
					<SystemButton data-annie-target="broadcast-send" disabled={sendingDisabled} onClick={handleSendMessage} loading={isLoading || isSuccess}>
						{t("send")}
					</SystemButton>
				</Box>
			</Box>
			{showBackWarning && (
				<SystemDialog
					open={showBackWarning}
					onClose={toggleBackWarning}
					title={t("discard.message")}
					body={t("discard.message.body")}
					// Discard: closes with no `didSend`, and NOT `props.onClose` directly — the dialog hands its
					// confirm a click event, which would arrive as a truthy `didSend`.
					onConfirm={() => props.onClose()}
					confirm={t("discard")}
					zIndex={1303 /* sit above this modal's own 1302 */}
				/>
			)}
		</Box>
	);
};
